import { getItemDef } from "./items";
import { addCargoToShip, shipCargoBreakdown, shipCargoQuality, totalCargoCapacity, totalCargoMass } from "./ship";
import type { Ship } from "./types";

const AMMO_PRICES: Record<string, number> = {
  bullets: 0.6,
  shells: 2.4,
  slugs: 9,
  missiles: 60,
};

const _resourcePriceCache = new Map<string, number>();
export function resourcePricePerKg(resource: string): number {
  const cached = _resourcePriceCache.get(resource);
  if (cached != null) return cached;
  let price = AMMO_PRICES[resource] ?? 0;
  if (price <= 0) {
    try {
      const def = getItemDef(`chunk_${resource}`);
      price = def.stats.valuePerKg;
    } catch {
      price = 0;
    }
  }
  _resourcePriceCache.set(resource, price);
  return price;
}

export const shipCargoCapacityKg = totalCargoCapacity;
export const shipCargoMassKg = totalCargoMass;

export function unionCargoResources(a: Ship, b: Ship): string[] {
  const set = new Set<string>();
  for (const k of shipCargoBreakdown(a).keys()) set.add(k);
  for (const k of shipCargoBreakdown(b).keys()) set.add(k);
  return [...set].sort();
}

interface TransferOutcome {
  movedKg: number;
  /** Credits charged to the buyer (0 in board mode). */
  creditsCharged: number;
}

export function transferResource(
  seller: Ship,
  buyer: Ship,
  resource: string,
  kg: number,
  mode: "trade" | "board",
): TransferOutcome {
  if (kg <= 0) return { movedKg: 0, creditsCharged: 0 };
  const sellerStock = shipCargoBreakdown(seller).get(resource) ?? 0;
  const buyerRoom = shipCargoCapacityKg(buyer) - shipCargoMassKg(buyer);
  let allowed = Math.min(kg, sellerStock, Math.max(0, buyerRoom));
  if (allowed <= 0) return { movedKg: 0, creditsCharged: 0 };

  let charged = 0;
  if (mode === "trade") {
    const price = resourcePricePerKg(resource);
    if (price > 0) {
      const maxAffordableKg = price > 0 ? buyer.credits / price : allowed;
      if (maxAffordableKg < allowed) allowed = Math.max(0, maxAffordableKg);
      charged = allowed * price;
    }
  }
  if (allowed <= 0) return { movedKg: 0, creditsCharged: 0 };

  const meanQuality = shipCargoQuality(seller, resource);
  const removed = drawResourceFromShip(seller, resource, allowed);
  const added = addCargoToShip(buyer, resource, removed, meanQuality);
  // Return any rounding remainder so books balance.
  if (added < removed) addCargoToShip(seller, resource, removed - added, meanQuality);

  if (mode === "trade" && charged > 0 && added > 0) {
    const actualCharge = (added / allowed) * charged;
    buyer.credits -= actualCharge;
    seller.credits += actualCharge;
    return { movedKg: added, creditsCharged: actualCharge };
  }
  return { movedKg: added, creditsCharged: 0 };
}

function drawResourceFromShip(ship: Ship, resource: string, kg: number): number {
  if (kg <= 0) return 0;
  let left = kg;
  for (const m of ship.modules) {
    if (m.hp <= 0 || !m.cargo) continue;
    const have = m.cargo.get(resource) ?? 0;
    if (have <= 0) continue;
    const take = Math.min(have, left);
    const newHave = have - take;
    m.cargo.set(resource, newHave);
    if (m.cargoQualityKg) {
      const qkg = m.cargoQualityKg.get(resource) ?? 0;
      m.cargoQualityKg.set(resource, qkg * (newHave / have));
    }
    left -= take;
    if (left <= 0) break;
  }
  return kg - left;
}

